import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  compareFacilityCustomerGrowth,
  type GrowthInsightsResponse,
} from "@/libs/analyticsApi";

const completedEightWeekWindow = () => {
  const currentMonday = new Date();
  currentMonday.setUTCHours(0, 0, 0, 0);
  currentMonday.setUTCDate(
    currentMonday.getUTCDate() - ((currentMonday.getUTCDay() + 6) % 7),
  );
  const anchor = Date.UTC(1970, 0, 5);
  const elapsedDays = Math.floor((currentMonday.getTime() - anchor) / 86_400_000);
  const endBoundary = new Date(currentMonday);
  endBoundary.setUTCDate(endBoundary.getUTCDate() - (elapsedDays % 56));
  const start = new Date(endBoundary);
  start.setUTCDate(start.getUTCDate() - 56);
  const lastIncludedDay = new Date(endBoundary);
  lastIncludedDay.setUTCDate(lastIncludedDay.getUTCDate() - 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: lastIncludedDay.toISOString().slice(0, 10),
  };
};
const asInstant = (date: string) => `${date}T00:00:00Z`;
const afterInclusiveDate = (date: string) => {
  const end = new Date(asInstant(date));
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
};
const formatUtcDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(new Date(value));
const lastIncludedDate = (exclusiveEnd: string) => {
  const date = new Date(exclusiveEnd);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString();
};
const dateProps = {
  fullWidth: true,
  size: "small" as const,
  slotProps: { inputLabel: { shrink: true } },
  type: "date",
};

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }} variant="h5">
        {value}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
    </Box>
  );
}

export function GrowthInsightsWorkspace() {
  const { user } = useAuth();
  const orgView = useMyOrg();
  const { facility, facilityId, isLoading: facilityLoading } = useResolvedFacility();
  const initialWindow = useRef(completedEightWeekWindow()).current;
  const [start, setStart] = useState(initialWindow.start);
  const [end, setEnd] = useState(initialWindow.end);
  const [result, setResult] = useState<GrowthInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"unavailable" | "forbidden" | "invalid" | "retry" | null>(null);
  const generation = useRef(0);
  const activeFacilityId = useRef(facilityId);
  activeFacilityId.current = facilityId;
  const organizationOwner = Boolean(user?.uid && orgView.org?.owner_user_id === user.uid);

  useEffect(() => {
    generation.current += 1;
    setResult(null);
    setError(null);
    setLoading(false);
  }, [facilityId]);

  const editWindow = (
    setter: (value: string) => void,
    value: string,
  ) => {
    generation.current += 1;
    setter(value);
    setResult(null);
    setError(null);
    setLoading(false);
  };

  const load = async () => {
    if (!facilityId || loading) return;
    const requestFacility = facilityId;
    const requestGeneration = ++generation.current;
    const current = () =>
      generation.current === requestGeneration && activeFacilityId.current === requestFacility;
    setLoading(true);
    setError(null);
    try {
      const response = await compareFacilityCustomerGrowth(requestFacility, {
        start: asInstant(start),
        end: afterInclusiveDate(end),
      });
      if (!current()) return;
      if (response.facility_id !== requestFacility) {
        setError("retry");
        return;
      }
      setResult(response);
    } catch (cause) {
      if (!current()) return;
      setResult(null);
      if (isApiError(cause) && cause.status === 503) setError("unavailable");
      else if (isApiError(cause) && cause.status === 403) setError("forbidden");
      else if (isApiError(cause) && cause.status === 422) setError("invalid");
      else setError("retry");
    } finally {
      if (current()) setLoading(false);
    }
  };

  if (facilityLoading || !facilityId) {
    return (
      <Stack aria-label="Loading customer insights" role="status" spacing={1} sx={{ alignItems: "center", justifyContent: "center", minHeight: "40vh" }}>
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">Loading…</Typography>
      </Stack>
    );
  }

  const messages = {
    unavailable: "Customer insights aren’t enabled for this organization. Reservations, campaigns, receipts, and exports remain available.",
    forbidden: "You don’t have permission to view internal customer insights.",
    invalid: "Choose one canonical 8-week UTC period, Monday through Sunday.",
    retry: "We couldn’t build this customer report. Review the dates and try again.",
  };

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}>
        <Box>
          <Typography component="h1" sx={{ fontWeight: 800 }} variant="h5">Customers &amp; campaigns</Typography>
          <Typography color="text.secondary" variant="body2">
            Understand repeat court reservations and post-campaign return at {facility?.name ?? "this facility"}.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button component={RouterLink} size="small" to="/analytics/insights/retention" variant="outlined">
            Service retention
          </Button>
          <Chip color="primary" icon={<GroupsOutlinedIcon />} label="Insights module" variant="outlined" />
        </Stack>
      </Stack>

      <Paper component="form" onSubmit={(event) => { event.preventDefault(); void load(); }} sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
        <Stack spacing={2}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 700 }} variant="h6">Select a completed period</Typography>
            <Typography color="text.secondary" variant="body2">
            Select a canonical 8-week UTC period, Monday through Sunday. The report compares reservation customers with the preceding non-overlapping 8 weeks, and campaign return rates need a complete 30-day observation window.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField {...dateProps} label="Start date" onChange={(event) => editWindow(setStart, event.target.value)} value={start} />
            <TextField {...dateProps} label="Last included day" onChange={(event) => editWindow(setEnd, event.target.value)} value={end} />
          </Stack>
          <Button disabled={loading} sx={{ alignSelf: "flex-start" }} type="submit" variant="contained">
            {loading ? "Building report…" : "Build customer report"}
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert
          action={error === "unavailable" && organizationOwner ? (
            <Button color="inherit" component={RouterLink} size="small" to="/settings/contract">Review modules</Button>
          ) : error === "retry" ? (
            <Button color="inherit" onClick={() => void load()} size="small">Retry</Button>
          ) : undefined}
          severity={error === "unavailable" ? "info" : "error"}
        >
          {messages[error]}
        </Alert>
      ) : null}

      {result ? (
        <Stack spacing={2.5}>
          <Box>
            <Typography color="text.secondary" variant="body2">
              Included period: {formatUtcDate(result.start)}–{formatUtcDate(lastIncludedDate(result.end))}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Generated {formatUtcDate(result.generated_at)} · {result.freshness}
            </Typography>
          </Box>
          <Typography color="text.secondary" variant="body2">{result.decision_supported}</Typography>
          {result.customer_summary ? (
            <Paper component="section" sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
              <Stack spacing={2}>
                <Box>
                  <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">Reservation customer mix</Typography>
                  <Typography color="text.secondary" variant="body2">Returning customers also reserved in the immediately preceding equal period.</Typography>
                </Box>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" } }}>
                  <Metric label="Customers" value={result.customer_summary.distinct_customers} />
                  <Metric label="Returning" value={result.customer_summary.returning_customers} />
                  <Metric label="New in this comparison" value={result.customer_summary.new_customers} />
                  <Metric label="Returning share" value={`${result.customer_summary.returning_share_percent}%`} />
                </Box>
              </Stack>
            </Paper>
          ) : (
            <Alert severity="info">No reportable customer cohort. Every included week must meet the privacy minimum before counts are shown.</Alert>
          )}

          <Stack component="section" spacing={1.5}>
            <Box>
              <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">Campaign customer return</Typography>
              <Typography color="text.secondary" variant="body2">This is post-redemption return behavior—not ad impressions or marketing conversion.</Typography>
            </Box>
            {result.campaigns.length ? result.campaigns.map((campaign) => (
              <Paper component="article" key={campaign.campaign_id} sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <CampaignOutlinedIcon color="primary" />
                    <Typography sx={{ fontWeight: 800 }} variant="subtitle1">{campaign.campaign_name}</Typography>
                  </Stack>
                  <LinearProgress aria-label={`${campaign.campaign_name} return rate`} value={campaign.return_rate_percent} variant="determinate" />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <Metric label="Eligible campaign customers" value={campaign.eligible_redeemers} />
                    <Metric label="Returned within 30 days" value={campaign.returned_within_30_days} />
                    <Metric label="30-day return rate" value={`${campaign.return_rate_percent}%`} />
                  </Stack>
                </Stack>
              </Paper>
            )) : (
              <Alert severity="info">No campaign cohort has both a complete 30-day observation window and enough customers to report safely.</Alert>
            )}
          </Stack>

          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">How to read this report</Typography>
            <Stack component="ul" spacing={0.75} sx={{ color: "text.secondary", mb: 0, mt: 1, pl: 2.5 }}>
              {result.limitations.map((limitation) => <Typography component="li" key={limitation} variant="body2">{limitation}</Typography>)}
            </Stack>
          </Paper>
        </Stack>
      ) : null}
    </Stack>
  );
}
